// pages/admin/DepositOrdersPage.tsx
// ⭐ P2P Deposit System (Admin) — รายการ deposit orders
// ยึดตาม src/components/admin/DESIGN_RULES.md

import React, { useState, useEffect, useCallback, useRef } from 'react';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { merchantApi } from '../../api/config';
import { toast } from 'react-toastify';
import socketService from '../../services/socketService';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faReceipt, faClock, faCheckCircle, faTimesCircle, faChartBar, faBan, faLock,
  faBuilding, faUniversity, faRotate, faSearch, faTimes, faHandHoldingDollar,
  faArrowRightArrowLeft, faCloudUpload, faImage,
} from '@fortawesome/free-solid-svg-icons';
import DataTable, { Column } from '../../components/shared/DataTable';
import { SlipAttemptTimeline, slipAttemptUrls } from '../../components/shared';
import { LiveViewToggle, LiveModeBufferBadge } from '../../components/shared/LiveViewToggle';
import MerchantMultiSelect from '../../components/shared/MerchantMultiSelect';
import {
  UiPageHeader, UiFilterPanel, UiStatsGrid, UiModal, UiButton, StatusBadge,
} from '../../components/ui';
import type { StatCardDef, StatusDef } from '../../components/ui';
import bankLogos from 'thai-banks-logo/banks-logo.json';

dayjs.extend(utc);
dayjs.extend(timezone);
const TZ = 'Asia/Bangkok';

const bankNameToLogoKey: Record<string, string> = {
  KBANK: 'KBANK', KTB: 'KTB', SCB: 'SCB', BBL: 'BBL', BAY: 'BAY',
  TTB: 'TTB', TMB: 'TTB', GSB: 'GSB', GHB: 'GHB', BAAC: 'BAAC',
  CIMB: 'CIMB', CIMBT: 'CIMB', UOB: 'UOB', UOBT: 'UOB', TISCO: 'TISCO',
  KKP: 'KKP', KKB: 'KKP', LHB: 'LHB', ICBC: 'ICBC', TCRB: 'TCRB',
};

const DEPOSIT_STATUS_MAP: Record<string, StatusDef> = {
  pending:   { bg: 'bg-amber-100', text: 'text-amber-700', icon: faClock,       label: 'รอชำระ' },
  paid:      { bg: 'bg-green-100', text: 'text-green-700', icon: faCheckCircle, label: 'สำเร็จ' },
  expired:   { bg: 'bg-gray-100',  text: 'text-gray-600',  icon: faTimesCircle, label: 'หมดอายุ' },
  expired_locked: { bg: 'bg-amber-100', text: 'text-amber-700', icon: faLock,   label: 'หมดอายุ (ล็อก)' },
  cancelled: { bg: 'bg-gray-100',  text: 'text-gray-600',  icon: faBan,         label: 'ยกเลิก' },
};

interface PayoutInfo {
  id: string;
  payout_number: string;
  status: string;
  amount: number;
  allocated_amount?: number;
  funded_amount?: number;
  bank_account_name?: string;
  bank_account_number?: string;
  bank_name?: string;
  window_expires_at?: string | null;
  created_at?: string;
  completed_at?: string | null;
}

interface DepositOrder {
  id: string;
  order_number: string;
  reference_id?: string;
  amount: number;
  fee_amount?: number;
  net_amount?: number;
  status: 'pending' | 'paid' | 'expired' | 'cancelled' | 'expired_locked';
  routed_to_payout: boolean;
  payout_id?: string | null;
  destination_bank_name?: string;
  destination_account_number?: string;
  destination_account_name?: string;
  expires_at: string;
  paid_at?: string;
  created_at: string;
  merchant?: { id: string; name: string; email: string };
  payout?: PayoutInfo | null;
}

// สถานะ payout (P2P liquidity) — ใช้ใน modal รายละเอียด payout
const PAYOUT_STATUS_MAP: Record<string, StatusDef> = {
  pending: { bg: 'bg-amber-100 dark:bg-amber-500/15', text: 'text-amber-700 dark:text-amber-400', icon: faClock, label: 'รอจับคู่' },
  p2p_completed: { bg: 'bg-green-100 dark:bg-green-500/15', text: 'text-green-700 dark:text-green-400', icon: faCheckCircle, label: 'สำเร็จ' },
  p2p_partial: { bg: 'bg-indigo-100 dark:bg-indigo-500/15', text: 'text-indigo-700 dark:text-indigo-400', icon: faClock, label: 'รอเว็บโอน' },
  pending_transfer: { bg: 'bg-blue-100 dark:bg-blue-500/15', text: 'text-blue-700 dark:text-blue-400', icon: faClock, label: 'Export แล้ว รอยืนยันโอน' },
  completed: { bg: 'bg-green-100 dark:bg-green-500/15', text: 'text-green-700 dark:text-green-400', icon: faCheckCircle, label: 'สำเร็จ' },
  expired: { bg: 'bg-gray-100 dark:bg-slate-700', text: 'text-gray-600 dark:text-slate-300', icon: faTimesCircle, label: 'หมดอายุ' },
  cancelled: { bg: 'bg-gray-100 dark:bg-slate-700', text: 'text-gray-600 dark:text-slate-300', icon: faBan, label: 'ยกเลิก' },
};

const BankCell: React.FC<{ name?: string; account?: string; holder?: string }> = ({ name, account, holder }) => {
  const logoKey = bankNameToLogoKey[(name || '').toUpperCase()] as keyof typeof bankLogos | undefined;
  const icon = logoKey ? bankLogos[logoKey]?.icon : null;
  return (
    <div className="flex items-center gap-2 min-w-0 max-w-[220px]">
      {icon ? (
        <img src={icon} alt={name} className="w-8 h-8 rounded-lg object-contain flex-shrink-0" />
      ) : (
        <div className="w-8 h-8 rounded-lg bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center text-purple-600 dark:text-purple-400 flex-shrink-0">
          <FontAwesomeIcon icon={faUniversity} className="text-xs" />
        </div>
      )}
      <div className="min-w-0">
        <p className="text-xs text-gray-500 dark:text-slate-400">{name || '-'}</p>
        <code className="text-xs bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-slate-300 px-1.5 py-0.5 rounded tabular-nums">{account || '-'}</code>
        <p className="text-xs text-gray-700 dark:text-slate-200 mt-0.5 truncate">{holder || '-'}</p>
      </div>
    </div>
  );
};

const MerchantCell: React.FC<{ merchant?: { name: string; email: string } }> = ({ merchant }) => (
  <div className="flex items-center gap-2.5 min-w-0">
    <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center text-blue-600 flex-shrink-0">
      <FontAwesomeIcon icon={faBuilding} className="text-xs" />
    </div>
    <div className="min-w-0">
      <p className="font-medium text-gray-800 dark:text-slate-100 text-sm truncate">{merchant?.name || '-'}</p>
      <p className="text-xs text-gray-400 dark:text-slate-500 truncate">{merchant?.email || ''}</p>
    </div>
  </div>
);

const AdminDepositOrdersPage: React.FC = () => {
  // ── deposit orders state ──
  const [orders, setOrders] = useState<DepositOrder[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedMerchantId, setSelectedMerchantId] = useState('');
  const [merchants, setMerchants] = useState<{ id: string; name: string; email: string }[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalItems, setTotalItems] = useState(0);
  const [counts, setCounts] = useState({ pending: 0, paid: 0, expired: 0, total: 0 });

  // ── date filter (default = วันนี้) ──
  const [dateFrom, setDateFrom] = useState(() => dayjs().tz(TZ).format('YYYY-MM-DD'));
  const [dateTo, setDateTo] = useState(() => dayjs().tz(TZ).format('YYYY-MM-DD'));
  const [appliedDateFrom, setAppliedDateFrom] = useState(() => dayjs().tz(TZ).format('YYYY-MM-DD'));
  const [appliedDateTo, setAppliedDateTo] = useState(() => dayjs().tz(TZ).format('YYYY-MM-DD'));

  // ── modal ──
  const [modal, setModal] = useState<{ type: 'approve' | 'expire' | 'release'; order: DepositOrder } | null>(null);
  const [payoutModal, setPayoutModal] = useState<PayoutInfo | null>(null);
  const [detailOrder, setDetailOrder] = useState<DepositOrder | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [slipFile, setSlipFile] = useState<File | null>(null);
  const [slipPreview, setSlipPreview] = useState('');
  const [skipVerify, setSkipVerify] = useState(false);
  const slipInputRef = useRef<HTMLInputElement>(null);

  const adminUser = JSON.parse(localStorage.getItem('admin_user') || '{}');
  const canSkipVerify = !adminUser.type || adminUser.role === 'super_admin' || adminUser.permissions?.approve_slip_no_verify === true;

  const [tick, setTick] = useState(0);
  useEffect(() => { const t = setInterval(() => setTick((v) => v + 1), 1000); return () => clearInterval(t); }, []);

  useEffect(() => {
    const delay = setTimeout(() => { setSearchTerm(searchInput); setCurrentPage(1); }, 400);
    return () => clearTimeout(delay);
  }, [searchInput]);

  useEffect(() => {
    merchantApi.get('/merchants').then((res) => setMerchants(res.data.data || [])).catch(() => {});
  }, []);

  // showLoading=false = refresh เงียบ (ไม่โชว์ skeleton) — ใช้กับทุกเส้นที่ไม่ใช่ user เปลี่ยน filter/กด Refresh
  const abortRef = useRef<AbortController | null>(null);
  const loadOrders = useCallback(async (showLoading = true) => {
    // last-request-wins: ยกเลิก request ก่อนหน้า กัน response เก่ามาทับข้อมูลใหม่ (pattern เดียวกับ AllOrdersPage)
    if (abortRef.current) abortRef.current.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    try {
      if (showLoading) setLoading(true);
      const params = new URLSearchParams();
      if (statusFilter) params.append('status', statusFilter);
      if (searchTerm) params.append('search', searchTerm);
      if (selectedMerchantId) params.append('merchant_id', selectedMerchantId);
      if (appliedDateFrom) params.append('start_date', appliedDateFrom);
      if (appliedDateTo) params.append('end_date', appliedDateTo);
      params.append('limit', String(pageSize));
      params.append('offset', String((currentPage - 1) * pageSize));

      const res = await merchantApi.get(`/admin/deposit-orders?${params.toString()}`, { signal: controller.signal });
      if (!controller.signal.aborted && res.data.success) {
        let list: DepositOrder[] = res.data.data || [];
        // กัน race: response ที่ DB snapshot ก่อนแถวเพิ่ง prepend จาก socket จะไม่มีแถวนั้น — ใส่กลับ (event ไม่ยิงซ้ำ)
        const nowMs = Date.now();
        recentPrependsRef.current.forEach((v, k) => { if (nowMs - v.at > 15000) recentPrependsRef.current.delete(k); });
        if (currentPage === 1 && !searchTerm && (!statusFilter || statusFilter === 'pending')) {
          const missing = Array.from(recentPrependsRef.current.values())
            .filter((v) => !list.some((o) => o.id === v.row.id)).map((v) => v.row);
          if (missing.length) list = [...missing, ...list].slice(0, pageSize);
        }
        setOrders(list);
        setTotalItems(res.data.total || 0);
      }
    } catch (e: any) {
      if (e?.name !== 'AbortError' && e?.code !== 'ERR_CANCELED') {
        toast.error('โหลดรายการ deposit ไม่สำเร็จ');
      }
    } finally {
      // เคลียร์เสมอเมื่อไม่ถูก abort (ไม่ดูแค่ showLoading) — silent call ที่ abort โหลดแบบ skeleton ต้องเป็นคนปิด loading แทน
      if (!controller.signal.aborted) setLoading(false);
    }
  }, [statusFilter, searchTerm, selectedMerchantId, appliedDateFrom, appliedDateTo, currentPage, pageSize]);

  const loadCounts = useCallback(async () => {
    const p = new URLSearchParams();
    if (selectedMerchantId) p.append('merchant_id', selectedMerchantId);
    if (appliedDateFrom) p.append('start_date', appliedDateFrom);
    if (appliedDateTo) p.append('end_date', appliedDateTo);
    // ยิงนัดเดียว — server นับ 4 สถานะให้ (แทนที่ browser ยิง 4 requests)
    try {
      const res = await merchantApi.get(`/admin/deposit-orders/summary?${p.toString()}`);
      if (res.data.success && res.data.data) { setCounts(res.data.data); return; }
    } catch { /* backend เก่ายังไม่มี /summary → fallback ด้านล่าง */ }
    // fallback: backend เก่า → นับทีละสถานะแบบเดิม (4 requests) ให้ยังทำงานได้ทุก tenant
    try {
      const mk = (s?: string) => {
        const q = new URLSearchParams(p);
        if (s) q.append('status', s);
        q.append('limit', '1');
        return merchantApi.get(`/admin/deposit-orders?${q.toString()}`).then((r) => r.data.total || 0);
      };
      const [pending, paid, expired, total] = await Promise.all([mk('pending'), mk('paid'), mk('expired'), mk()]);
      setCounts({ pending, paid, expired, total });
    } catch { /* ignore */ }
  }, [selectedMerchantId, appliedDateFrom, appliedDateTo]);

  // ── ⭐ Live mode (pattern AllOrdersPage): event → merge/prepend เข้า state ตรง ๆ ไม่ refetch → ตารางไม่กระพริบ ──
  const [realtimeMode, setRealtimeMode] = useState(true);
  const [bufferedCount, setBufferedCount] = useState(0);
  // แถวที่เพิ่ง prepend จาก socket (id → row) — ให้ loadOrders ใส่กลับถ้า response เก่ากว่าแถว (ดูใน loadOrders)
  const recentPrependsRef = useRef<Map<string, { row: DepositOrder; at: number }>>(new Map());
  // state ปัจจุบันผ่าน ref — handler ใช้ดูสถานะเดิมของแถวก่อน merge (คำนวณการ์ดสถิติแบบรู้ transition จริง)
  const ordersRef = useRef(orders);
  useEffect(() => { ordersRef.current = orders; }, [orders]);
  // กันนับการ์ดสถิติซ้ำเมื่อ event เดียวกันมาหลายรอบ/หลาย path (deposit-paid กับ deposit-updated{paid} ใช้ Set ร่วมผ่าน order_number)
  const countedCreatedRef = useRef<Set<string>>(new Set());
  const countedPaidRef = useRef<Set<string>>(new Set());
  const countedExpiredRef = useRef<Set<string>>(new Set());

  // loaders ผ่าน ref — socket effect ใช้ deps นิ่ง ไม่ re-subscribe ตาม identity ของ loader
  const loadOrdersRef = useRef(loadOrders);
  const loadCountsRef = useRef(loadCounts);
  useEffect(() => { loadOrdersRef.current = loadOrders; loadCountsRef.current = loadCounts; }, [loadOrders, loadCounts]);

  // fallback refetch เงียบ (debounce 600ms) — เฉพาะ event ที่ payload ไม่พอ merge (backend เก่ายังไม่แนบ row)
  const refreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const scheduleSilentRefresh = useCallback(() => {
    if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    refreshTimerRef.current = setTimeout(() => {
      refreshTimerRef.current = null;
      loadOrdersRef.current(false);
      loadCountsRef.current();
    }, 600);
  }, []);
  useEffect(() => () => {
    if (refreshTimerRef.current) clearTimeout(refreshTimerRef.current);
    abortRef.current?.abort();
  }, []);

  const toggleRealtimeMode = () => {
    if (realtimeMode) {
      // กำลังพัก live — ยกเลิก refetch เงียบที่ค้างคิว ไม่ให้ตารางขยับเองใน View mode
      if (refreshTimerRef.current) { clearTimeout(refreshTimerRef.current); refreshTimerRef.current = null; }
    } else if (bufferedCount > 0) {
      loadOrders(false); loadCounts();
      setBufferedCount(0);
      toast.info(`อัพเดท ${bufferedCount} รายการล่าสุด`);
    }
    setRealtimeMode((v) => !v);
  };

  const handleSearch = () => {
    if (dayjs(dateFrom).isAfter(dayjs(dateTo), 'day')) { toast.error('วันที่เริ่มต้นต้องน้อยกว่าหรือเท่ากับวันที่สิ้นสุด'); return; }
    setAppliedDateFrom(dateFrom);
    setAppliedDateTo(dateTo);
    setCurrentPage(1);
  };

  const handleReset = () => {
    const today = dayjs().tz(TZ).format('YYYY-MM-DD');
    setDateFrom(today); setDateTo(today);
    setAppliedDateFrom(today); setAppliedDateTo(today);
    setSearchInput(''); setSearchTerm(''); setStatusFilter(''); setSelectedMerchantId('');
    setCurrentPage(1);
  };

  useEffect(() => { loadOrders(); loadCounts(); }, [loadOrders, loadCounts]);

  // ⭐ realtime: merge/prepend จาก payload ตรง ๆ (ไม่ refetch — ตารางไม่กระพริบ) · payload บาง (backend เก่า) → refetch เงียบ
  useEffect(() => {
    // จำลองเงื่อนไข filter ฝั่ง server ก่อนแตะ state (แถวใหม่เกิดเป็น pending เสมอ)
    const matchesMerchant = (mid?: string) => !selectedMerchantId || (!!mid && selectedMerchantId.split(',').includes(mid));
    const matchesDateToday = () => {
      const t = dayjs().tz(TZ).format('YYYY-MM-DD');
      return (!appliedDateFrom || appliedDateFrom <= t) && (!appliedDateTo || appliedDateTo >= t);
    };
    // การ์ดสถิติปรับแบบ optimistic — แม่นเมื่อเห็นแถวเดิมใน list (รู้ transition จริง + แถวใน list = ผ่าน filter ของ counts แล้ว)
    // ไม่เห็นแถว: deposit-paid (มี merchant_id) เดา pending→paid ภายใต้ guard · deposit-updated (ไม่มี merchant_id) ไม่แตะการ์ดเลย
    // กันนับข้ามร้าน/ข้ามวัน และกันหัก pending ซ้ำของใบที่ไม่เคยอยู่ pending (approve ใบ expired_locked ฯลฯ)
    const markPaid = (key: string, prev: DepositOrder | undefined, merchantId?: string) => {
      if (countedPaidRef.current.has(key)) return;
      countedPaidRef.current.add(key);
      if (prev) {
        setCounts((c) => ({
          ...c,
          paid: c.paid + 1,
          pending: prev.status === 'pending' ? Math.max(0, c.pending - 1) : c.pending,
          expired: prev.status === 'expired' ? Math.max(0, c.expired - 1) : c.expired,
        }));
        return;
      }
      if (merchantId == null) return; // deposit-updated ไม่มี merchant_id — ไม่รู้ scope ไม่แตะการ์ด (รอ refresh ปกติ)
      if (!matchesMerchant(merchantId) || !matchesDateToday()) return;
      setCounts((c) => ({ ...c, pending: Math.max(0, c.pending - 1), paid: c.paid + 1 }));
    };

    const onCreated = (p: any) => {
      if (!realtimeMode) { setBufferedCount((c) => c + 1); return; }
      const inScope = matchesMerchant(p?.merchant_id) && matchesDateToday();
      if (inScope && p?.deposit_order_id && !countedCreatedRef.current.has(p.deposit_order_id)) {
        countedCreatedRef.current.add(p.deposit_order_id);
        setCounts((c) => ({ ...c, pending: c.pending + 1, total: c.total + 1 }));
      }
      const row: DepositOrder | undefined = p?.row;
      if (row?.id && row.order_number) {
        // prepend เฉพาะตอน list ปัจจุบันมองเห็นแถวนี้ (server ก็ไม่คืนมันในมุมมองอื่น)
        if (!inScope || (statusFilter && statusFilter !== 'pending') || searchTerm) return;
        recentPrependsRef.current.set(row.id, { row, at: Date.now() }); // ให้ refetch ที่ snapshot เก่ากว่าใส่แถวกลับ
        setCurrentPage((prevPage) => {
          if (prevPage === 1) {
            setOrders((prev) => (prev.some((o) => o.id === row.id) ? prev : [row, ...prev.slice(0, pageSize - 1)]));
          }
          return prevPage;
        });
        setTotalItems((t) => t + 1);
      } else {
        scheduleSilentRefresh(); // backend เก่ายังไม่แนบ row
      }
    };

    const onPaid = (p: any) => {
      if (!realtimeMode) { setBufferedCount((c) => c + 1); return; }
      if (!p?.order_number && !p?.id) { scheduleSilentRefresh(); return; }
      const prev = ordersRef.current.find((o) => (p.id ? o.id === p.id : o.order_number === p.order_number));
      setOrders((prevList) => prevList.map((o) => (
        (p.id ? o.id === p.id : o.order_number === p.order_number)
          ? {
              ...o, status: 'paid' as const,
              paid_at: p.paid_at || new Date().toISOString(),
              ...(p.fee_amount != null ? { fee_amount: Number(p.fee_amount), net_amount: Number(p.net_amount ?? o.amount) } : {}),
            }
          : o)));
      markPaid(p.order_number || p.id, prev, p.merchant_id);
      // แถวหลุด filter สถานะปัจจุบัน (เช่นดู tab รอชำระ) → refetch เงียบเอาออกให้ view ตรง server
      if (prev && statusFilter && statusFilter !== 'paid') scheduleSilentRefresh();
    };

    const onUpdated = (p: any) => {
      if (!realtimeMode) { setBufferedCount((c) => c + 1); return; }
      if (!p?.id || !p?.status) { scheduleSilentRefresh(); return; }
      const prev = ordersRef.current.find((o) => o.id === p.id);
      setOrders((prevList) => prevList.map((o) => (o.id === p.id
        ? { ...o, status: p.status, ...(p.paid_at ? { paid_at: p.paid_at } : {}) }
        : o)));
      if (p.status === 'paid') {
        markPaid(p.order_number || p.id, prev); // admin approve ยิง event นี้แทน deposit-paid — Set ร่วมกันกันนับซ้ำ
      } else if (p.status === 'expired' || p.status === 'expired_locked') {
        // ปรับการ์ดเฉพาะเมื่อเห็นแถวเดิม (รู้ transition จริง — กันหัก pending ซ้ำตอน expired_locked→expired และกันนับข้ามร้าน)
        if (prev && !countedExpiredRef.current.has(`${p.id}:${p.status}`)) {
          countedExpiredRef.current.add(`${p.id}:${p.status}`);
          setCounts((c) => ({
            ...c,
            pending: prev.status === 'pending' ? Math.max(0, c.pending - 1) : c.pending,
            // การ์ด "หมดอายุ" query status='expired' เป๊ะ — expired_locked ไม่บวก
            expired: p.status === 'expired' ? c.expired + 1 : c.expired,
          }));
        }
      }
      if (prev && statusFilter && p.status !== statusFilter) scheduleSilentRefresh();
    };

    socketService.on('deposit-created', onCreated);
    socketService.on('deposit-paid', onPaid);
    socketService.on('deposit-updated', onUpdated);
    return () => {
      socketService.off('deposit-created', onCreated);
      socketService.off('deposit-paid', onPaid);
      socketService.off('deposit-updated', onUpdated);
    };
  }, [realtimeMode, statusFilter, searchTerm, selectedMerchantId, appliedDateFrom, appliedDateTo, pageSize, scheduleSilentRefresh]);

  // reset slip state เมื่อปิด modal
  useEffect(() => {
    if (!modal) { setSlipFile(null); setSlipPreview(''); setSkipVerify(false); }
  }, [modal]);

  const handleSlipSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) { toast.error('กรุณาเลือกไฟล์รูปภาพ'); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error('ไฟล์ใหญ่เกิน 5MB'); return; }
    setSlipFile(file);
    const reader = new FileReader();
    reader.onloadend = () => setSlipPreview(reader.result as string);
    reader.readAsDataURL(file);
  };

  const fileToBase64 = (file: File) => new Promise<string>((resolve, reject) => {
    const r = new FileReader();
    r.onloadend = () => resolve(r.result as string);
    r.onerror = reject;
    r.readAsDataURL(file);
  });

  const handleConfirm = async () => {
    if (!modal) return;

    // expire → ยืนยันธรรมดา
    if (modal.type === 'expire') {
      try {
        setSubmitting(true);
        const res = await merchantApi.patch(`/admin/deposit-orders/${modal.order.id}/expire`);
        if (res.data.success) { toast.success('Force expire สำเร็จ'); setModal(null); loadOrders(false); loadCounts(); }
      } catch (e: any) {
        toast.error(e.response?.data?.message || 'เกิดข้อผิดพลาด');
      } finally { setSubmitting(false); }
      return;
    }

    // release → ปล่อย locked คืน capacity (ถ้า payout ยัง pending → reset window 15m)
    if (modal.type === 'release') {
      try {
        setSubmitting(true);
        const res = await merchantApi.patch(`/admin/deposit-orders/${modal.order.id}/release`);
        if (res.data.success) { toast.success('ปล่อย capacity คืนสำเร็จ'); setModal(null); loadOrders(false); loadCounts(); }
      } catch (e: any) {
        toast.error(e.response?.data?.message || 'เกิดข้อผิดพลาด');
      } finally { setSubmitting(false); }
      return;
    }

    // approve → ต้องแนบสลิป + ตรวจสอบ (เช็คบัญชี payout)
    if (!slipFile) { toast.error('กรุณาแนบสลิปการโอน'); return; }
    try {
      setSubmitting(true);
      const imageBase64 = await fileToBase64(slipFile);
      const res = await merchantApi.patch(`/admin/deposit-orders/${modal.order.id}/approve`, {
        imageBase64,
        skip_verification: skipVerify,
      });
      if (res.data.success) { toast.success('อนุมัติ Deposit สำเร็จ เงินเข้า Wallet แล้ว'); setModal(null); loadOrders(false); loadCounts(); }
    } catch (e: any) {
      toast.error(e.response?.data?.message || 'ตรวจสอบสลิปไม่สำเร็จ');
    } finally {
      setSubmitting(false);
    }
  };

  const secondsLeft = (expiresAt: string) => Math.max(0, Math.floor((new Date(expiresAt).getTime() - Date.now()) / 1000));
  const fmtCountdown = (s: number) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

  const statCards: StatCardDef[] = [
    { icon: faClock, iconClassName: 'bg-amber-50 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400', label: 'รอชำระ', value: <span className="text-amber-600 dark:text-amber-400">{counts.pending.toLocaleString()}</span> },
    { icon: faCheckCircle, iconClassName: 'bg-green-50 dark:bg-green-900/30 text-green-600 dark:text-green-400', label: 'สำเร็จ', value: <span className="text-green-600 dark:text-green-400">{counts.paid.toLocaleString()}</span> },
    { icon: faTimesCircle, iconClassName: 'bg-gray-100 dark:bg-slate-700 text-gray-500 dark:text-slate-400', label: 'หมดอายุ', value: <span className="text-gray-600 dark:text-slate-300">{counts.expired.toLocaleString()}</span> },
    { icon: faChartBar, iconClassName: 'text-white', iconStyle: { background: 'var(--theme-primary-gradient)' }, label: 'ทั้งหมด', value: <span style={{ color: 'var(--theme-primary)' }}>{counts.total.toLocaleString()}</span> },
  ];

  const orderColumns: Column<DepositOrder>[] = [
    {
      key: 'order_number', header: 'เลขที่', width: '170px',
      render: (d) => (
        <div className="min-w-0">
          <p className="font-semibold text-gray-800 dark:text-slate-100 text-sm truncate">{d.order_number}</p>
          {d.reference_id && <p className="text-xs text-gray-500 dark:text-slate-400 truncate">Ref: {d.reference_id}</p>}
        </div>
      ),
    },
    { key: 'merchant', header: 'Merchant', width: '180px', render: (d) => <MerchantCell merchant={d.merchant} /> },
    {
      key: 'amount', header: 'จำนวนเงิน', width: '130px',
      render: (d) => (
        <div>
          <span className="font-bold text-sm tabular-nums" style={{ color: 'var(--theme-accent)' }}>฿{Number(d.amount).toLocaleString()}</span>
          {(d.fee_amount || 0) > 0 && (
            d.routed_to_payout
              ? <p className="text-[10px] text-gray-400 dark:text-slate-500 tabular-nums">ค่าธรรมเนียม P2P ฿{Number(d.fee_amount).toLocaleString()} · หักจากกระเป๋า fee</p>
              : <p className="text-[10px] text-gray-400 dark:text-slate-500 tabular-nums">fee ฿{Number(d.fee_amount).toLocaleString()} · net ฿{Number(d.net_amount ?? d.amount).toLocaleString()} (เข้า Wallet)</p>
          )}
        </div>
      ),
    },
    {
      key: 'destination', header: 'บัญชีปลายทาง', width: '260px',
      render: (d) => (
        <div>
          <BankCell name={d.destination_bank_name} account={d.destination_account_number} holder={d.destination_account_name} />
          <span className={`mt-1 inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium ${d.routed_to_payout ? 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300' : 'bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-slate-300'}`}>
            <FontAwesomeIcon icon={d.routed_to_payout ? faArrowRightArrowLeft : faBuilding} className="text-[9px]" />
            {d.routed_to_payout ? 'P2P (payout)' : 'บัญชีเว็บ'}
          </span>
        </div>
      ),
    },
    {
      key: 'payout', header: 'Payout', width: '140px',
      render: (d) => (
        d.payout?.payout_number
          ? (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setPayoutModal(d.payout!); }}
              title="ดูรายละเอียด payout"
              className="text-[11px] font-mono bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 px-1.5 py-1 rounded hover:bg-indigo-100 dark:hover:bg-indigo-900/50 hover:underline transition-colors cursor-pointer"
            >{d.payout.payout_number}</button>
          )
          : <span className="text-xs text-gray-300 dark:text-slate-600">— (บัญชีเว็บ)</span>
      ),
    },
    { key: 'status', header: 'สถานะ', width: '110px', render: (d) => <StatusBadge status={d.status} statusMap={DEPOSIT_STATUS_MAP} /> },
    {
      key: 'countdown', header: 'หมดอายุ', width: '90px',
      render: (d) => {
        if (d.status !== 'pending') return <span className="text-xs text-gray-400 dark:text-slate-500">-</span>;
        const s = secondsLeft(d.expires_at);
        return <span className={`text-xs font-semibold tabular-nums ${s === 0 ? 'text-red-500' : 'text-amber-600 dark:text-amber-400'}`}>{s === 0 ? 'หมดเวลา' : fmtCountdown(s)}</span>;
      },
    },
    {
      key: 'created_at', header: 'วันที่', width: '100px',
      render: (d) => (
        <div className="whitespace-nowrap">
          <p className="text-sm text-gray-700 dark:text-slate-200">{dayjs(d.created_at).format('DD/MM/YYYY')}</p>
          <p className="text-xs text-gray-400 dark:text-slate-500">{dayjs(d.created_at).format('HH:mm')}</p>
        </div>
      ),
    },
    {
      key: 'actions', header: 'Actions', width: '170px',
      render: (d) => (
        d.status === 'pending' ? (
          <div className="flex items-center gap-1 flex-wrap">
            <button
              onClick={(e) => { e.stopPropagation(); setModal({ type: 'approve', order: d }); }}
              className="px-2 py-1 rounded-md text-[11px] font-medium bg-green-50 dark:bg-green-900/30 text-green-600 dark:text-green-400 hover:bg-green-100 dark:hover:bg-green-900/50 transition-colors"
            >อนุมัติ (มือ)</button>
            <button
              onClick={(e) => { e.stopPropagation(); setModal({ type: 'expire', order: d }); }}
              className="px-2 py-1 rounded-md text-[11px] font-medium bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-slate-300 hover:bg-gray-200 dark:hover:bg-slate-600 transition-colors"
            >หมดอายุ</button>
          </div>
        ) : d.status === 'expired_locked' ? (
          <div className="flex items-center gap-1 flex-wrap">
            <button
              onClick={(e) => { e.stopPropagation(); setModal({ type: 'approve', order: d }); }}
              className="px-2 py-1 rounded-md text-[11px] font-medium bg-green-50 dark:bg-green-900/30 text-green-600 dark:text-green-400 hover:bg-green-100 dark:hover:bg-green-900/50 transition-colors"
            >อนุมัติ (แนบสลิป)</button>
            <button
              onClick={(e) => { e.stopPropagation(); setModal({ type: 'release', order: d }); }}
              className="px-2 py-1 rounded-md text-[11px] font-medium bg-amber-50 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400 hover:bg-amber-100 dark:hover:bg-amber-900/50 transition-colors"
            >ปล่อยคืน</button>
          </div>
        ) : d.status === 'expired' && !d.routed_to_payout ? (
          // ⭐ expired + เข้าบัญชีเว็บ (custodial) → admin แนบสลิปอนุมัติย้อนหลังให้ลูกค้าได้
          //    (backend เช็คอายุสลิปเทียบช่วงเวลาของ order แล้ว lock → settle ให้เอง; ฝั่ง P2P payout ไม่เปิดเพราะ payout อาจถูก fund/ปิดไปแล้ว)
          <button
            onClick={(e) => { e.stopPropagation(); setModal({ type: 'approve', order: d }); }}
            className="px-2 py-1 rounded-md text-[11px] font-medium bg-green-50 dark:bg-green-900/30 text-green-600 dark:text-green-400 hover:bg-green-100 dark:hover:bg-green-900/50 transition-colors"
          >อนุมัติย้อนหลัง (แนบสลิป)</button>
        ) : <span className="text-xs text-gray-300 dark:text-slate-600">-</span>
      ),
    },
  ];

  return (
    <div className="max-w-full">
      <UiPageHeader
        icon={faReceipt}
        title="P2P Deposit Orders"
        subtitle={`รายการฝากเงินผ่าน payout liquidity • ${counts.total.toLocaleString()} รายการ`}
        below={<LiveViewToggle realtimeMode={realtimeMode} onToggle={toggleRealtimeMode} />}
      />

      <UiFilterPanel
        dateFrom={dateFrom}
        dateTo={dateTo}
        onDateFromChange={setDateFrom}
        onDateToChange={setDateTo}
        onSearch={handleSearch}
        onReset={handleReset}
        loading={loading}
        actions={<UiButton variant="secondary" icon={faRotate} onClick={() => { loadOrders(); loadCounts(); }}>Refresh</UiButton>}
      >
        <div className="flex-1 min-w-[220px]">
          <label className="block text-xs font-medium text-gray-500 dark:text-slate-400 mb-1"><FontAwesomeIcon icon={faSearch} className="mr-1" />ค้นหา</label>
          <div className="relative">
            <input
              type="text" placeholder="เลขที่ Deposit Order / Reference ID..." value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-200 dark:focus:ring-slate-600 focus:border-gray-400 dark:focus:border-slate-500 pr-7"
            />
            {searchInput && <button type="button" onClick={() => setSearchInput('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-red-500"><FontAwesomeIcon icon={faTimes} className="text-xs" /></button>}
          </div>
        </div>
        <div className="flex-1 min-w-[140px]">
          <label className="block text-xs font-medium text-gray-500 dark:text-slate-400 mb-1">สถานะ</label>
          <select
            value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}
            className="w-full px-3 py-2 border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-200 dark:focus:ring-slate-600 focus:border-gray-400 dark:focus:border-slate-500"
          >
            <option value="">ทุกสถานะ</option>
            <option value="pending">รอชำระ</option>
            <option value="paid">สำเร็จ</option>
            <option value="expired">หมดอายุ</option>
            <option value="expired_locked">หมดอายุ (ล็อก)</option>
            <option value="cancelled">ยกเลิก</option>
          </select>
        </div>
        <div className="flex-1 min-w-[180px]">
          <label className="block text-xs font-medium text-gray-500 dark:text-slate-400 mb-1">Merchant</label>
          <MerchantMultiSelect merchants={merchants} value={selectedMerchantId} onChange={(v) => { setSelectedMerchantId(v); setCurrentPage(1); }} variant="light" />
        </div>
      </UiFilterPanel>

      <UiStatsGrid cards={statCards} loading={loading && counts.total === 0} cols={4} />

      {/* buffer โหมด View — กดเพื่อดึงรายการที่ค้างระหว่างพัก live */}
      <LiveModeBufferBadge
        count={bufferedCount}
        onFlush={() => { loadOrders(false); loadCounts(); setBufferedCount(0); toast.success('อัพเดทข้อมูลแล้ว'); }}
      />

      <DataTable
        data={orders}
        columns={orderColumns}
        loading={loading}
        emptyMessage="ไม่มีรายการ Deposit"
        emptyIcon={<FontAwesomeIcon icon={faHandHoldingDollar} className="text-5xl text-gray-200 dark:text-slate-600" />}
        serverPagination={{ currentPage, totalItems, pageSize, onPageChange: setCurrentPage, onPageSizeChange: (s) => { setPageSize(s); setCurrentPage(1); } }}
        rowClassName={(d) => (d.status === 'pending' ? 'bg-amber-50/40 dark:bg-amber-900/10' : '')}
        onRowClick={(d) => setDetailOrder(d)}
        hoverable compact
      />

      <UiModal
        open={!!modal}
        onClose={() => setModal(null)}
        title={modal?.type === 'approve' ? 'อนุมัติ Deposit (แนบสลิป + ตรวจสอบ)' : modal?.type === 'release' ? 'ปล่อย capacity คืน' : 'บังคับหมดอายุ Deposit'}
        icon={modal?.type === 'approve' ? faCheckCircle : faTimesCircle}
        iconColor={modal?.type === 'approve' ? 'text-green-500' : 'text-gray-500'}
        size="md"
        scrollable
        footer={
          <div className="flex gap-3">
            <UiButton variant="secondary" onClick={() => setModal(null)} disabled={submitting} className="flex-1 justify-center">ยกเลิก</UiButton>
            <UiButton
              variant={modal?.type === 'expire' ? 'danger' : 'primary'}
              loading={submitting}
              disabled={modal?.type === 'approve' && !slipFile}
              onClick={handleConfirm}
              className={`flex-1 justify-center ${modal?.type === 'approve' ? '!bg-green-600 hover:!bg-green-700' : ''}`}
            >ยืนยัน</UiButton>
          </div>
        }
      >
        {modal && (
          <div className="space-y-4">
            <div className="bg-gray-50 dark:bg-slate-700/50 rounded-xl p-4 space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-gray-500 dark:text-slate-400">เลขที่:</span><strong className="text-gray-800 dark:text-slate-100">{modal.order.order_number}</strong></div>
              <div className="flex justify-between"><span className="text-gray-500 dark:text-slate-400">จำนวนเงิน:</span><strong className="tabular-nums" style={{ color: 'var(--theme-accent)' }}>฿{Number(modal.order.amount).toLocaleString()}</strong></div>
              <div className="flex justify-between"><span className="text-gray-500 dark:text-slate-400">บัญชีปลายทาง:</span><span className="text-gray-800 dark:text-slate-200 text-right">{modal.order.destination_bank_name}<br/>{modal.order.destination_account_number}<br/>{modal.order.destination_account_name}</span></div>
            </div>

            {modal.type === 'expire' ? (
              <p className="text-xs text-gray-500 dark:text-slate-400">บังคับหมดอายุรายการนี้ → คืน capacity ให้ payout เพื่อจับคู่ใหม่</p>
            ) : modal.type === 'release' ? (
              <p className="text-xs text-gray-500 dark:text-slate-400">ปล่อย capacity ของรายการนี้คืนให้ payout → ถ้า payout ยังไม่หมดอายุจะตั้งเวลาใหม่ 15 นาที (จับคู่ต่อได้)</p>
            ) : (
              <>
                <div>
                  <label className="block text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wide mb-1.5">
                    <FontAwesomeIcon icon={faCloudUpload} className="mr-1" /> แนบสลิปการโอน *
                  </label>
                  <div
                    className={`relative border-2 border-dashed rounded-xl p-5 text-center cursor-pointer transition-all
                      ${slipPreview ? 'border-green-400 bg-green-50 dark:bg-green-900/10' : 'border-gray-300 dark:border-slate-600 hover:border-green-400 hover:bg-gray-50 dark:hover:bg-slate-700/50'}`}
                    onClick={() => slipInputRef.current?.click()}
                  >
                    <input ref={slipInputRef} type="file" accept="image/*" onChange={handleSlipSelect} className="hidden" />
                    {slipPreview ? (
                      <div className="space-y-2">
                        <div className="relative inline-block">
                          <img src={slipPreview} alt="slip" className="max-h-40 rounded-lg mx-auto shadow" />
                          <button type="button" onClick={(e) => { e.stopPropagation(); setSlipFile(null); setSlipPreview(''); if (slipInputRef.current) slipInputRef.current.value=''; }}
                            className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600">
                            <FontAwesomeIcon icon={faTimes} className="text-xs" />
                          </button>
                        </div>
                        <p className="text-xs text-green-600 dark:text-green-400">{slipFile?.name}</p>
                      </div>
                    ) : (
                      <div className="space-y-1.5">
                        <FontAwesomeIcon icon={faImage} className="text-2xl text-gray-400 dark:text-slate-500" />
                        <p className="text-sm text-gray-600 dark:text-slate-300">คลิกเพื่อเลือกรูปสลิป</p>
                        <p className="text-xs text-gray-400 dark:text-slate-500">ระบบจะตรวจสลิป + เช็คเลขบัญชีปลายทาง (payout) อัตโนมัติ</p>
                      </div>
                    )}
                  </div>
                </div>

                {canSkipVerify && (
                  <label className="flex items-start gap-2 text-xs text-gray-600 dark:text-slate-300 cursor-pointer">
                    <input type="checkbox" checked={skipVerify} onChange={(e) => setSkipVerify(e.target.checked)} className="mt-0.5" />
                    <span><strong className="text-amber-600 dark:text-amber-400">ข้ามการตรวจสอบสลิป</strong> (เฉพาะ super admin) — ยังต้องแนบรูปไว้เป็นหลักฐาน แต่ไม่เช็คยอด/บัญชี/ความถูกต้อง</span>
                  </label>
                )}
              </>
            )}
          </div>
        )}
      </UiModal>

      {/* ⭐ รายละเอียด payout ที่ deposit นี้จับคู่ (คลิกจากคอลัมน์ Payout) */}
      <UiModal
        open={!!payoutModal}
        onClose={() => setPayoutModal(null)}
        title="รายละเอียด Payout (สภาพคล่อง)"
        icon={faArrowRightArrowLeft}
        iconColor="text-indigo-500"
        size="md"
      >
        {payoutModal && (() => {
          const amount = Number(payoutModal.amount || 0);
          const funded = Number(payoutModal.funded_amount || 0);
          const allocated = Number(payoutModal.allocated_amount || 0);
          const shortfall = Math.max(0, Math.round((amount - funded) * 100) / 100);
          const pct = (v: number) => (amount > 0 ? Math.min(100, (v / amount) * 100) : 0);
          return (
            <div className="space-y-3 text-sm">
              <div className="flex items-center justify-between">
                <code className="text-xs bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-slate-200 px-2 py-1 rounded">{payoutModal.payout_number}</code>
                <StatusBadge status={payoutModal.status} statusMap={PAYOUT_STATUS_MAP} />
              </div>

              {/* progress การเติมยอด */}
              <div>
                <div className="flex h-2 w-full overflow-hidden rounded-full bg-gray-100 dark:bg-slate-700">
                  <div className="bg-green-500" style={{ width: `${pct(funded)}%` }} title={`เติมแล้ว ฿${funded.toLocaleString()}`} />
                  <div className="bg-amber-400" style={{ width: `${pct(allocated)}%` }} title={`จองค้าง ฿${allocated.toLocaleString()}`} />
                </div>
                <div className="mt-1 flex justify-between text-[10px] tabular-nums text-gray-500 dark:text-slate-400">
                  <span className="text-green-600 dark:text-green-400">เติมแล้ว ฿{funded.toLocaleString()}</span>
                  <span>ส่วนค้าง ฿{shortfall.toLocaleString()}</span>
                </div>
              </div>

              <div className="bg-gray-50 dark:bg-slate-700/50 rounded-xl p-4 space-y-2">
                <div className="flex justify-between"><span className="text-gray-500 dark:text-slate-400">ยอดถอนรวม:</span><strong className="tabular-nums" style={{ color: 'var(--theme-accent)' }}>฿{amount.toLocaleString()}</strong></div>
                <div className="flex justify-between"><span className="text-gray-500 dark:text-slate-400">เติมแล้ว (P2P):</span><span className="text-green-600 dark:text-green-400 tabular-nums">฿{funded.toLocaleString()}</span></div>
                {allocated > 0 && (
                  <div className="flex justify-between"><span className="text-gray-500 dark:text-slate-400"><FontAwesomeIcon icon={faLock} className="mr-1 text-[10px] text-amber-500" />deposit จองค้าง:</span><span className="text-amber-600 dark:text-amber-400 tabular-nums">฿{allocated.toLocaleString()}</span></div>
                )}
                <div className="flex justify-between pt-2 border-t border-gray-200 dark:border-slate-600"><span className="text-gray-500 dark:text-slate-400">บัญชีรับเงิน:</span>
                  <span className="text-gray-800 dark:text-slate-200 text-right">{payoutModal.bank_name}<br/>{payoutModal.bank_account_number}<br/>{payoutModal.bank_account_name}</span>
                </div>
              </div>

              <div className="text-xs text-gray-400 dark:text-slate-500 space-y-0.5">
                {payoutModal.created_at && <p>สร้างเมื่อ: {dayjs(payoutModal.created_at).format('DD/MM/YYYY HH:mm')}</p>}
                {payoutModal.window_expires_at && !payoutModal.completed_at && (
                  <p>window จับคู่: {dayjs(payoutModal.window_expires_at).isBefore(dayjs()) ? 'หมดแล้ว' : `ถึง ${dayjs(payoutModal.window_expires_at).format('DD/MM HH:mm')}`}</p>
                )}
                {payoutModal.completed_at && <p>สำเร็จเมื่อ: {dayjs(payoutModal.completed_at).format('DD/MM/YYYY HH:mm')}</p>}
                <p className="pt-1">จัดการเต็มรูปแบบได้ที่หน้า <a href="/admin/p2p-payouts" className="text-indigo-500 hover:underline">P2P Payouts</a></p>
              </div>
            </div>
          );
        })()}
      </UiModal>

      {/* ⭐ รายละเอียด Deposit (คลิกจากแถวในตาราง) — สรุปรายการ + ประวัติการอัพสลิป */}
      <UiModal
        open={!!detailOrder}
        onClose={() => setDetailOrder(null)}
        title="รายละเอียด Deposit"
        icon={faReceipt}
        iconColor="text-indigo-500"
        size="lg"
        scrollable
      >
        {detailOrder && (
          <div className="space-y-4">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-xs text-gray-400 dark:text-slate-500 mb-0.5">เลขที่ Deposit</p>
                <p className="font-mono font-bold text-gray-800 dark:text-slate-100 text-sm break-all">{detailOrder.order_number}</p>
              </div>
              <div className="shrink-0"><StatusBadge status={detailOrder.status} statusMap={DEPOSIT_STATUS_MAP} /></div>
            </div>

            <div className="bg-gray-50 dark:bg-slate-700/50 rounded-xl p-4 space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-gray-500 dark:text-slate-400">เลขที่:</span><strong className="text-gray-800 dark:text-slate-100">{detailOrder.order_number}</strong></div>
              <div className="flex justify-between"><span className="text-gray-500 dark:text-slate-400">จำนวนเงิน:</span><strong className="tabular-nums" style={{ color: 'var(--theme-accent)' }}>฿{Number(detailOrder.amount).toLocaleString()}</strong></div>
              <div className="flex justify-between"><span className="text-gray-500 dark:text-slate-400">บัญชีปลายทาง:</span><span className="text-gray-800 dark:text-slate-200 text-right">{detailOrder.destination_bank_name}<br/>{detailOrder.destination_account_number}<br/>{detailOrder.destination_account_name}</span></div>
              <div className="flex flex-wrap gap-x-6 gap-y-1 pt-2 border-t border-gray-200 dark:border-slate-600">
                <div className="text-xs text-gray-400 dark:text-slate-400">สร้างเมื่อ: <span className="text-gray-600 dark:text-slate-300">{dayjs(detailOrder.created_at).format('DD/MM/YYYY HH:mm:ss')}</span></div>
                {detailOrder.paid_at && (
                  <div className="text-xs text-green-500">ชำระเมื่อ: <span className="text-green-600 font-medium">{dayjs(detailOrder.paid_at).format('DD/MM/YYYY HH:mm:ss')}</span></div>
                )}
              </div>
            </div>

            <SlipAttemptTimeline fetchUrl={slipAttemptUrls.adminOrder(detailOrder.id)} isAdmin />
          </div>
        )}
      </UiModal>
    </div>
  );
};

export default AdminDepositOrdersPage;
